import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, FileText, Clock, Package, Truck, Megaphone, Zap, Shield, Target } from 'lucide-react';

const SwiggyInstamartOnboarding = () => {
  const requiredDocuments = [
    'GST Certificate',
    'PAN Card',
    'Trademark Certificate (if available)',
    'FSSAI License (for food products)',
    'Mobile Number & Email ID'
  ];

  const onboardingSteps = [
    { step: '1', title: 'Documentation', description: 'Submit required documents for verification' },
    { step: '2', title: 'Brand Approval', description: 'Get your brand approved by Swiggy Instamart' },
    { step: '3', title: 'Catalog Setup', description: 'Create product listings with EAN codes' },
    { step: '4', title: 'Dark Store Assignment', description: 'Initial 30-40 dark stores in Bangalore' },
    { step: '5', title: 'Go Live', description: 'Start selling on Swiggy Instamart' }
  ];

  const pricing = {
    listingFee: '₹30,000',
    adCredits: '₹25,000',
    platformFee: '₹5,000',
    initialStores: '30-40 dark stores'
  };

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Swiggy Instamart Seller Onboarding | Start Selling on Instamart</title>
        <meta name="description" content="Complete Swiggy Instamart onboarding: brand approval, documentation & listing. ₹30,000/SKU with ₹25,000 ad credits. Quick commerce specialists India." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/swiggy-instamart-seller-onboarding" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-orange-500 font-medium">Swiggy Instamart Onboarding</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4 mr-2" />
                Quick Commerce Onboarding
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Swiggy Instamart <span className="text-orange-500">Seller Onboarding</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Complete onboarding support to launch your brand on Swiggy Instamart. 
                From documentation to going live, we handle everything.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?audit=true">
                  <Button className="bg-orange-500 text-white hover:bg-orange-600 px-8 py-6 rounded-full text-base font-semibold">
                    Start Onboarding
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-3xl p-8 md:p-12 text-white">
            <h2 className="font-heading text-3xl font-bold mb-8 text-center">Swiggy Instamart Listing Fees</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{pricing.listingFee}</div>
                <div className="text-orange-100">Per SKU</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{pricing.adCredits}</div>
                <div className="text-orange-100">Ad Credits</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">{pricing.platformFee}</div>
                <div className="text-orange-100">Platform Fee</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">30-40</div>
                <div className="text-orange-100">Initial Dark Stores</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Onboarding Steps */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Onboarding Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {onboardingSteps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold mb-6">Documents Required</h2>
              <ul className="space-y-4">
                {requiredDocuments.map((doc, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3" />
                    <span className="text-lg text-slate-700">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-orange-50 rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-bold mb-4">Why Choose Us?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Shield className="w-5 h-5 text-orange-500 mr-3 mt-1" />
                  <span>Expert guidance through approval process</span>
                </li>
                <li className="flex items-start">
                  <FileText className="w-5 h-5 text-orange-500 mr-3 mt-1" />
                  <span>Complete documentation support</span>
                </li>
                <li className="flex items-start">
                  <Package className="w-5 h-5 text-orange-500 mr-3 mt-1" />
                  <span>Optimized product listings</span>
                </li>
                <li className="flex items-start">
                  <Megaphone className="w-5 h-5 text-orange-500 mr-3 mt-1" />
                  <span>Ad campaign setup included</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Sell on Swiggy Instamart?
          </h2>
          <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
            Get complete onboarding support and start reaching millions of quick commerce customers.
          </p>
          <Link to="/contact?audit=true">
            <Button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Start Onboarding Now
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SwiggyInstamartOnboarding;
